import { SelectionModel } from '@angular/cdk/collections';
import { Component, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTable, MatTableDataSource } from '@angular/material/table';
import { CalendarEvent, CalendarEventVM } from './events';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { NotificationService } from 'app/_services/notification.service';
import { EditEventDialogComponent } from './edit-event/edit-event.component';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { DialogsService } from 'app/_services/dialogs/dialogs.service';
import { Category, CategoryTags } from 'app/categories/category';
import { CalendarEventsService } from 'app/_services/calendarEvents.service';
import { CategoryEventsService } from 'app/_services/category-events.service';

@Component({
    selector: 'app-events',
    templateUrl: './events.component.html',
    styleUrls: ['../shared/table-defaults.scss', './events.component.scss'],
    animations: [
        trigger('detailExpand', [
            state('collapsed', style({ height: '0px', minHeight: '0' })),
            state('expanded', style({ height: '*' })),
            transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
        ]),
    ],
})
export class EventsComponent implements OnInit {
    value = '';
    calendarEvents: CalendarEventVM[];
    eventNames: string[];
    categories: Category[];
    categoryTags: CategoryTags[];
    categoryNames: string[];
    eventsForm: FormGroup = new FormGroup({});
    dataSource: MatTableDataSource<CalendarEventVM>;
    selection = new SelectionModel<CalendarEventVM>(true, []);
    removeItems = false;
    expandedEvent: CalendarEventVM | null;

    categoryFilter = new FormControl('');
    filterValues = {
        category: '',
        searchText: '',
    };

    displayedColumns: string[] = ['eventName', 'eventDesc', 'categoryEvent', 'actions'];
    columnsToDisplay = this.displayedColumns.slice();

    @ViewChild(MatTable) table: MatTable<CalendarEventVM[]>;
    @ViewChild(MatPaginator) paginator!: MatPaginator;
    @ViewChild(MatSort) sort!: MatSort;

    constructor(
        private categoryService: CategoryEventsService,
        private calendarEventsService: CalendarEventsService,
        private dialog: MatDialog,
        private dialogSerivce: DialogsService,
        public notificationService: NotificationService
    ) {}

    ngOnInit(): void {
        // Watch category filter and update on changes
        this.categoryFilter.valueChanges.subscribe((category) => {
            let filter;

            // Whenever a category is selected, check if the dataSource has any filter values from predicate.
            if (this.dataSource.filter) {
                filter = JSON.parse(this.dataSource.filter);
            } else {
                // If not, then set filter to equal default filter object.
                filter = this.filterValues;
            }

            // Reassign values for category and searchText in filter object, then replace dataSource filter.
            this.filterValues.category = category || '';
            this.filterValues.searchText = filter.searchText || '';
            this.dataSource.filter = JSON.stringify(this.filterValues);
        });

        this.categoryService.getCategories().subscribe((categories) => {
            this.categories = categories.filter((c) => c.active);
            this.categoryTags = categories.map((cat: Category) => new CategoryTags(cat.name, cat.categoryId));
            this.categoryNames = this.categories.map((category) => category.name);
        });

        this.calendarEventsService.getCalendarEvents().subscribe((events) => {
            this.calendarEvents = events;
            this.loadDatasource(this.calendarEvents);
            this.dataSource.filterPredicate = this.filteredRows();
            this.setSortAndPagination();

            // Create list of calendar event names to pass into dialog for name validation.
            this.eventNames = this.calendarEvents.map((event) => event.eventName);
        });
    }

    loadDatasource(data: CalendarEventVM[]): void {
        this.dataSource = new MatTableDataSource(data);
    }

    setSortAndPagination(): void {
        this.dataSource.sort = this.sort;
        this.dataSource.paginator = this.paginator;
    }

    cssClass(category: string): string {
        return 'cat-' + category.trim().replace(/\s+/g, '-').toLowerCase();
    }

    newEvent(): CalendarEvent {
        return {
            catCommunity: false,
            catDepartmentIt: false,
            catParents: false,
            catSchools: false,
            catStaff: false,
            catStudents: false,
            eventContact: null,
            eventDesc: '',
            eventId: 0,
            eventName: 'Test',
            eventNotes: '',
            eventUrl: '',
            name: '',
        };
    }

    openEditEventDialog(event: CalendarEvent, eventNames: string[], title: string): MatDialogRef<EditEventDialogComponent> {
        const config: MatDialogConfig = {
            width: '80vw',
            maxWidth: '600px',
            panelClass: 'calendarEventEditor',
            data: { event, eventNames, title },
        };

        return this.dialog.open(EditEventDialogComponent, config);
    }

    addCalendarEvent(): void {
        const title = 'Add New Calendar Event';
        const event = this.newEvent();
        const events = this.calendarEvents;
        const eventNames = this.eventNames;

        const dialogResult = this.openEditEventDialog(event, eventNames, title);

        dialogResult.afterClosed().subscribe((result: CalendarEvent) => {
            if (!result) {
                return;
            }

            this.calendarEventsService.createCalendarEvent(result).subscribe((res: CalendarEvent) => {
                this.calendarEventsService.getCalEventVMById(res.eventId).subscribe((newEvent: CalendarEventVM) => {
                    events.push(newEvent);
                    this.loadDatasource(events);
                    this.setSortAndPagination();
                    this.notificationService.showSuccess(`New Calendar Event has been added.`);
                });
            });
        });
    }

    editCalendarEvent(event: CalendarEventVM): void {
        const title = 'Edit Calendar Event';
        const events = this.calendarEvents;
        const eventNames = this.eventNames;

        this.calendarEventsService.getCalEventById(event.eventId).subscribe((updatedEvent) => {
            if (!updatedEvent) {
                this.notificationService.showError(`ERROR: Calendar event with id ${event.eventId} could not be found.`);
                return;
            }

            const dialogResult = this.openEditEventDialog(updatedEvent, eventNames, title);

            dialogResult.afterClosed().subscribe((result: CalendarEvent) => {
                if (!result) {
                    return;
                }

                this.calendarEventsService.updateCalendarEvent(result.eventId, result).subscribe((res: CalendarEvent) => {
                    event.eventContact = res.eventContact;
                    event.eventDesc = res.eventDesc;
                    event.eventId = res.eventId;
                    event.eventName = res.eventName;
                    event.eventNotes = res.eventNotes;
                    event.eventUrl = res.eventUrl;
                    event.name = res.name;
                });

                this.loadDatasource(events);
                this.setSortAndPagination();
                this.notificationService.showSuccess(`Calendar Event has been updated.`);
            });
        });
    }

    deleteCalendarEvent(event: CalendarEventVM): void {
        const dialogResult$ = this.dialogSerivce.OpenConfirmationDialog({
            TitleText: 'Delete Calendar Event',
            MessageText:
                `Are you sure you want to delete this calendar event? Deleting the calendar event will also remove all 
                 event dates listed for that event, and all categories for that event. THIS CANNOT BE UNDONE!`,
            OKText: 'Yes, remove event',
            CancelText: 'No',
        });

        dialogResult$.subscribe((result) => {
            if (!result) {
                return;
            }

            const index = this.dataSource.data.indexOf(event, 0);

            if (index > -1) {
                this.dataSource.data.splice(index, 1);
            }

            this.calendarEventsService.deleteCalendarEvent(event.eventId).subscribe();
            this.table.renderRows();
            this.setSortAndPagination();
            this.notificationService.showSuccess(`Calendar Event have been removed.`);
        });
    }

    applySearchFilter(searchTerms: string): void {
        const filter = {
            category: this.filterValues.category,
            searchText: searchTerms,
        };
        this.dataSource.filter = JSON.stringify(filter);

        if (this.dataSource.paginator) {
            this.dataSource.paginator.firstPage();
        }
    }

    clearFilter(): void {
        this.dataSource.filter = '';

        if (this.dataSource.paginator) {
            this.dataSource.paginator.firstPage();
        }
    }

    filteredRows(): (data: CalendarEventVM, filter: string) => boolean {
        const filterFunction = (data: CalendarEventVM, filter: string): boolean => {
            // Parse filter from search bar
            const searchString = JSON.parse(filter);

            // Grab the names of each category in the event and add them to a new array.
            const categoryList = data.eventCategories.map((ec) => ec.name);

            // Find index of different search terms in filter string and return boolean result for each.
            // If the data properties or the selected category from the filter are null, skip finding their indexes.
            const category = searchString.category ? this.getIndexOfField(searchString.category, categoryList): false;
            const name = data.eventName ? this.getIndexOfField(searchString.searchText, data.eventName) : false;
            const desc = data.eventDesc ? this.getIndexOfField(searchString.searchText, data.eventDesc) : false;

            // If category is selected, filter event name and description within those categorized events.
            // Otherwise, search event name and description through all events.
            if (searchString.category) {
                return category && (name || desc);
            } else {
                return name || desc;
            }
        };
        return filterFunction;
    }

    getIndexOfField(searchString: string, data: any): boolean {
        return data.toString().trim().toLowerCase().indexOf(searchString.toLowerCase()) !== -1;
    }

    showNotification(type: string): void {
        switch (type) {
            case 'info':
                return this.notificationService.showInfo('Info');
            case 'success':
                return this.notificationService.showSuccess('Success!');
            case 'warning':
                return this.notificationService.showWarning('Warning!');
            case 'error':
                return this.notificationService.showError('Error!');
            default:
                return;
        }
    }
}
